/**
 * version.js — savoir qu'une nouvelle version est en ligne, sans l'imposer
 *
 * L'application est installée comme une PWA : tant que le technicien ne la
 * recharge pas, son téléphone exécute le code chargé la dernière fois. Le
 * 19/10/2026 l'horodatage des photos est resté absent de tous les comptes
 * rendus de la matinée pour cette seule raison — le code était en ligne
 * depuis la veille au soir, les téléphones l'ont pris entre 6 h et midi, au
 * gré des rechargements. Les photos déposées avant n'ont pas de bandeau.
 *
 * On surveille donc l'adresse de la page : `index.html` nomme le paquet
 * JavaScript courant, avec son empreinte. Si l'empreinte servie diffère de
 * celle que nous exécutons, une version est sortie.
 *
 * On ne recharge JAMAIS tout seul. Un rechargement au milieu d'un compte
 * rendu effacerait trente photos et une signature. On propose, le technicien
 * décide, et le bandeau attend aussi longtemps qu'il le faut.
 */

const INTERVALLE_MS = 10 * 60 * 1000;

/** L'empreinte du paquet que ce navigateur exécute en ce moment. */
function empreinteCourante() {
  const s = document.querySelector('script[src*="/static/js/main."]');
  if (!s) return null;
  const m = s.src.match(/main\.([a-z0-9]+)\.js/);
  return m ? m[1] : null;
}

/** L'empreinte du paquet que le serveur sert maintenant. */
async function empreinteServie() {
  // `no-store` est indispensable : sans lui le navigateur peut répondre
  // depuis son propre cache, et nous comparerions la page à elle-même.
  const r = await fetch('/index.html?v=' + Date.now(), { cache: 'no-store' });
  if (!r.ok) return null;
  const html = await r.text();
  const m = html.match(/main\.([a-z0-9]+)\.js/);
  return m ? m[1] : null;
}

/**
 * Prévient `quand` une seule fois, dès qu'une version différente est servie.
 *
 * On vérifie au retour sur l'application — c'est le moment où le technicien
 * finit une intervention et en ouvre une autre — et toutes les dix minutes.
 */
export function surveillerVersion(quand) {
  const ici = empreinteCourante();
  if (!ici) return () => {};   // page servie autrement : rien à comparer
  let prevenu = false;

  const verifier = async () => {
    if (prevenu || document.hidden) return;
    try {
      const la = await empreinteServie();
      if (la && la !== ici) { prevenu = true; quand(); }
    } catch { /* hors ligne : on retentera */ }
  };

  const t = setInterval(verifier, INTERVALLE_MS);
  document.addEventListener('visibilitychange', verifier);
  setTimeout(verifier, 15000);   // pas au premier rendu : l'écran d'abord

  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', verifier);
  };
}
